// Analytics Tracking Utilities
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    clarity?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
    ttq?: (...args: any[]) => void;
  }
}

// Google Analytics
export const loadGoogleAnalytics = (trackingId: string) => {
  if (typeof window === 'undefined' || window.gtag) {
    console.log('🔍 Google Analytics: Already loaded or server-side, skipping');
    return;
  }

  console.log('🚀 Loading Google Analytics with ID:', trackingId);

  const script1 = document.createElement('script');
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
  script1.async = true;
  script1.onload = () => console.log('✅ Google Analytics script loaded successfully');
  script1.onerror = () => console.error('❌ Failed to load Google Analytics script');

  const script2 = document.createElement('script');
  script2.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${trackingId}');
    console.log('✅ Google Analytics initialized with ID: ${trackingId}');
  `;

  document.head.appendChild(script1);
  document.head.appendChild(script2);
};

// Global click → dataLayer tracking.
// 1) Elements with explicit data-gtm-event attributes win (custom label/section).
// 2) Otherwise, any link/button inside <footer>, <header> or <nav> is auto-tracked
//    so all footer/nav links are covered without editing every layout component.
export const initClickTracking = () => {
  if (typeof window === 'undefined' || (window as any).__gtmClickInit) return;
  (window as any).__gtmClickInit = true;

  const push = (obj: Record<string, any>) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(obj);
  };

  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    // 1) Explicit tagging
    const tagged = target.closest('[data-gtm-event]') as HTMLElement | null;
    if (tagged) {
      push({
        event: tagged.dataset.gtmEvent,
        click_label: tagged.dataset.gtmLabel || tagged.textContent?.trim() || '',
        click_section: tagged.dataset.gtmSection || '',
        click_url: (tagged as HTMLAnchorElement).href || tagged.dataset.gtmUrl || '',
      });
      return;
    }

    // 2) Auto-track links/buttons inside footer / header / nav
    const clickable = target.closest('a, button') as HTMLElement | null;
    if (!clickable) return;
    const region = clickable.closest('footer, header, nav');
    if (!region) return;
    const regionName = region.tagName.toLowerCase(); // "footer" | "header" | "nav"
    push({
      event: regionName === 'footer' ? 'footer_click' : 'nav_click',
      click_label:
        clickable.getAttribute('aria-label') ||
        clickable.textContent?.trim() ||
        (clickable as HTMLImageElement).getAttribute?.('alt') ||
        '',
      click_section: regionName,
      click_url: (clickable as HTMLAnchorElement).href || '',
    });
  }, true);

  console.log('✅ GTM click tracking initialized');
};

// Google Tag Manager (container)
export const loadGTM = (containerId: string) => {
  // Skip if server-side, already loaded by us, or already present in the page
  // (e.g. server-rendered in the root layout).
  if (
    typeof window === 'undefined' ||
    (window as any).__gtmLoaded ||
    document.querySelector('script[src*="googletagmanager.com/gtm.js"]')
  ) {
    console.log('🔍 Google Tag Manager: Already loaded or server-side, skipping');
    return;
  }
  (window as any).__gtmLoaded = true;

  console.log('🚀 Loading Google Tag Manager with ID:', containerId);

  // Head script — initializes dataLayer and loads the GTM container
  const script = document.createElement('script');
  script.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${containerId}');
  `;
  document.head.insertBefore(script, document.head.firstChild);

  // noscript fallback iframe — placed at the top of <body>
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);

  console.log('✅ Google Tag Manager script added');
};

// Microsoft Clarity
export const loadClarity = (projectId: string) => {
  if (typeof window === 'undefined' || window.clarity) {
    console.log('🔍 Microsoft Clarity: Already loaded or server-side, skipping');
    return;
  }

  console.log('🚀 Loading Microsoft Clarity with ID:', projectId);

  const script = document.createElement('script');
  script.innerHTML = `
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      console.log('✅ Microsoft Clarity initialized with ID: ${projectId}');
    })(window, document, "clarity", "script", "${projectId}");
  `;

  document.head.appendChild(script);
  console.log('✅ Microsoft Clarity script added to head');
};

// Facebook Pixel
export const loadFacebookPixel = (pixelId: string) => {
  if (typeof window === 'undefined' || window.fbq) return;

  const script = document.createElement('script');
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `;

  document.head.appendChild(script);

  // Add noscript fallback
  const noscript = document.createElement('noscript');
  noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1" />`;
  document.head.appendChild(noscript);
};

// TikTok Pixel
export const loadTikTokPixel = (pixelId: string) => {
  if (typeof window === 'undefined' || window.ttq) return;

  const script = document.createElement('script');
  script.innerHTML = `
    !function (w, d, t) {
      w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
      ttq.load('${pixelId}');
      ttq.page();
    }(window, document, 'ttq');
  `;

  document.head.appendChild(script);
};

// Custom Scripts
export const loadCustomScripts = (headerScripts: string, footerScripts: string) => {
  if (typeof window === 'undefined') return;

  // Load header scripts
  if (headerScripts?.trim()) {
    const headerDiv = document.createElement('div');
    headerDiv.innerHTML = headerScripts;
    const scripts = headerDiv.querySelectorAll('script');
    scripts.forEach(script => {
      const newScript = document.createElement('script');
      if (script.src) {
        newScript.src = script.src;
        newScript.async = script.async;
      } else {
        newScript.innerHTML = script.innerHTML;
      }
      document.head.appendChild(newScript);
    });
  }

  // Load footer scripts
  if (footerScripts?.trim()) {
    const footerDiv = document.createElement('div');
    footerDiv.innerHTML = footerScripts;
    const scripts = footerDiv.querySelectorAll('script');
    scripts.forEach(script => {
      const newScript = document.createElement('script');
      if (script.src) {
        newScript.src = script.src;
        newScript.async = script.async;
      } else {
        newScript.innerHTML = script.innerHTML;
      }
      document.body.appendChild(newScript);
    });
  }
};

// Main Analytics Loader
export const loadAnalytics = async () => {
  console.log('🔍 Starting analytics configuration load...');
  
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5000/api'}/public/site/analytics`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const analytics = await response.json();
    console.log('📊 Analytics configuration loaded:', analytics);

    // Click → dataLayer tracking (works with the data-gtm-* attributes on links/buttons)
    initClickTracking();

    // Google Tag Manager (load early so it can manage other tags)
    if (analytics.gtm?.enabled && analytics.gtm?.containerId) {
      console.log('🚀 Google Tag Manager enabled, loading...');
      loadGTM(analytics.gtm.containerId);
    } else {
      console.log('⚠️ Google Tag Manager disabled or missing container ID');
    }

    // Google Analytics — only load gtag.js directly when GTM is NOT present.
    // When GTM is active it owns GA4 (configure the Google tag inside GTM), so loading
    // gtag.js here too would double-fire page_view and cause "deferred hits" conflicts.
    const gtmPresent = typeof document !== 'undefined' &&
      !!document.querySelector('script[src*="googletagmanager.com/gtm.js"]');
    if (gtmPresent) {
      console.log('ℹ️ GTM present — GA4 is handled via GTM, skipping direct gtag.js load');
    } else if (analytics.googleAnalytics?.enabled && analytics.googleAnalytics?.trackingId) {
      console.log('🚀 Google Analytics enabled, loading...');
      loadGoogleAnalytics(analytics.googleAnalytics.trackingId);
    } else {
      console.log('⚠️ Google Analytics disabled or missing tracking ID');
    }

    // Microsoft Clarity
    if (analytics.clarity?.enabled && analytics.clarity?.projectId) {
      console.log('🚀 Microsoft Clarity enabled, loading...');
      loadClarity(analytics.clarity.projectId);
    } else {
      console.log('⚠️ Microsoft Clarity disabled or missing project ID');
    }

    // Facebook Pixel
    if (analytics.facebookPixel?.enabled && analytics.facebookPixel?.pixelId) {
      loadFacebookPixel(analytics.facebookPixel.pixelId);
    }

    // TikTok Pixel
    if (analytics.tiktokPixel?.enabled && analytics.tiktokPixel?.pixelId) {
      loadTikTokPixel(analytics.tiktokPixel.pixelId);
    }

    // Custom Scripts
    if (analytics.customScripts) {
      loadCustomScripts(
        analytics.customScripts.headerScripts,
        analytics.customScripts.footerScripts
      );
    }
    
    console.log('✅ Analytics loading completed successfully');
  } catch (error) {
    console.error('❌ Failed to load analytics:', error);
  }
};